import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import clsx from 'clsx'
import { useFormikContext } from 'formik'

import dayjs from 'dayjs'
import localeData from 'dayjs/plugin/localeData'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCampground, faCaravan, faHiking, faHouseUser, faUserFriends } from '@fortawesome/free-solid-svg-icons'
import { buttonColors } from '../../utils/Button'
import Label from '../../utils/Label'

dayjs.extend(localeData)

export const siteTypeOptions = [
  {
    text: 'RV Sites',
    short: 'RV Sites',
    description: "Only sites suitable for RV's",
    value: 'rv',
    values: ['rv'],
    icon: faCaravan
  },
  {
    text: 'Tent or RV',
    short: 'Tent/RV',
    description: 'You can tent in an RV site',
    value: 'rv_tent',
    values: ['rv', 'tent'],
    icon: faCampground
  },
  {
    text: 'Tents Only',
    short: 'Tents',
    description: "No RV's",
    value: 'tent',
    values: ['tent', 'tent_walk_in', 'tentWalkIn'],
    icon: faHiking
  },
  {
    text: 'Group Sites',
    short: 'Groups',
    description: 'You and your friends',
    value: 'group',
    values: ['group'],
    icon: faUserFriends
  },
  {
    text: 'Other',
    short: 'Other',
    description: 'Cabins, Day Use, etc',
    value: 'other',
    values: ['other', 'cabin'],
    icon: faHouseUser
  }
]

const SiteType = () => {
  const requestForm = useFormikContext()

  const facility = useSelector(store => store.facilities.facility)
  const type = useMemo(() => requestForm.values.type, [requestForm.values.type])

  const siteTypeOptionsReduced = useMemo(() => {
    if (!facility.sitesDetails) {
      return []
    }

    const facilitySiteTypes = Object.keys(facility.sitesDetails.types)

    const availOptions = siteTypeOptions.filter(option => {
      const isMatch = [...new Set(facilitySiteTypes)].filter(x => new Set(option.values).has(x))
      return isMatch.length > 0
    })

    if (!availOptions.map((opt) => opt.value).includes(requestForm.values.type)) {
      availOptions[0] && requestForm.setFieldValue('type', availOptions[0].value)
    }
    return availOptions
  }, [facility])

  const handleTypeClick = newType => {
    requestForm.setFieldValue('type', newType)
    requestForm.setFieldValue('water', false)
    requestForm.setFieldValue('sewer', false)
    requestForm.setFieldValue('minElectric', undefined)
    requestForm.setFieldValue('minLength', '')
    requestForm.setFieldValue('ignoreAda', true)
    requestForm.setFieldValue('sitePremium', false)
    requestForm.setFieldValue('specificSiteIds', [])
  }

  return (
    <>
      <Label small>Site Type</Label>
      <div className="flex items-stretch">
        {siteTypeOptionsReduced.map(option => (
          <div className="flex w-1/5" key={option.text}>
            <div
              className={clsx(
                'w-full rounded-md border-2 text-center cursor-pointer ',
                'p-1 sm:px-2 sm:py-3',
                'mr-1 md:mr-3',
                option.value === type ? buttonColors['green'] : buttonColors['gray']
              )}
              onClick={() => handleTypeClick(option.value)}
            >
              <FontAwesomeIcon icon={option.icon} className="text-xl sm:text-3xl md:text-6xl mt-1" />
              <h3 className="mt-1 md:mt-2 font-semibold tracking-tighter md:tracking-tight text-sm">
                <span className="hidden sm:inline">{option.text}</span>
                <span className="inline sm:hidden">{option.short}</span>
              </h3>
            </div>
          </div>
        ))}
      </div>
    </>
  )
}

export default SiteType
